import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, LogOut, PanelLeftClose, PanelLeft, ChevronDown, Sparkles } from "lucide-react";
import { NAV_GROUPS } from "../config/nav";
import { useAuth } from "../context/AuthContext";
import { initials } from "../lib/utils-format";
import api from "../lib/api";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import NotificationBell from "./NotificationBell";
import { AskAngela } from "./AskAngela";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

function SidebarContent({ collapsed, onNavigate, menus, isAdmin, badges }) {
  const groups = NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.adminOnly ? isAdmin : (item.always || item.to === "/app" || menus.includes(item.menuKey || item.to))
      ),
    }))
    .filter((group) => group.items.length > 0);
  const loc = useLocation();
  // Viewing the comm channels from the AI Office card (/ai-comms?channels=1) should keep
  // AI Office highlighted, not Communication.
  const commsFromOffice = loc.pathname === "/ai-comms" && new URLSearchParams(loc.search).get("channels") === "1";
  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center gap-2.5 px-4 h-16 border-b border-border ${collapsed ? "justify-center" : ""}`}>
        {collapsed ? (
          <img src="/logo-icon.jpg" alt="Arqitek" className="h-9 w-9 rounded-lg object-cover shrink-0" data-testid="sidebar-logo-icon" />
        ) : (
          <img src="/logo.png" alt="Arqitek" className="h-7 w-auto object-contain" data-testid="sidebar-logo" />
        )}
      </div>
      <nav className="flex-1 overflow-y-auto slim-scroll py-4 px-3 space-y-6">
        {groups.map((group, gi) => (
          <div key={group.label || `group-${gi}`}>
            {!collapsed && group.label && (
              <div className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/app"}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => {
                    let active = isActive;
                    if (item.to === "/ai-comms") active = loc.pathname === "/ai-comms" && !commsFromOffice;
                    if (item.to === "/ai-office") active = isActive || commsFromOffice;
                    return `relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors duration-200 ${
                      active
                        ? "bg-primary/10 text-primary before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-0.5 before:rounded-full before:bg-primary"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                    } ${collapsed ? "justify-center" : ""}`;
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                  {badges?.[item.to] > 0 && (
                    <span
                      data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center ${collapsed ? "absolute top-1 right-1" : "ml-auto"}`}
                    >
                      {badges[item.to] > 99 ? "99+" : badges[item.to]}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, menus, isAdmin } = useAuth();
  const [angelaOpen, setAngelaOpen] = useState(false);
  const [myQueueCount, setMyQueueCount] = useState(0);
  const [myNewLeadsCount, setMyNewLeadsCount] = useState(0);
  const [buyBoxNewCount, setBuyBoxNewCount] = useState(0);
  const [sellLandNewCount, setSellLandNewCount] = useState(0);

  const loadQueue = useCallback(() => {
    if (!user?.id) return;
    api.get(`/work-items?assignee_id=${user.id}&limit=500`)
      .then((r) => {
        const open = ["pending", "claimed", "in_progress", "snoozed"];
        setMyQueueCount((r.data.items || []).filter((w) => w.assignee_id === user.id && open.includes(w.status)).length);
      })
      .catch(() => {});
    api.get(`/leads?owner_id=${user.id}&lead_status=New&limit=500`)
      .then((r) => setMyNewLeadsCount((r.data.items || []).length))
      .catch(() => {});
    api.get(`/buybox/submissions/new-count`)
      .then((r) => setBuyBoxNewCount(r.data.count || 0))
      .catch(() => {});
    api.get(`/sell-land/submissions/new-count`)
      .then((r) => setSellLandNewCount(r.data.count || 0))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    loadQueue();
    const t = setInterval(loadQueue, 60000);
    return () => clearInterval(t);
  }, [loadQueue]);

  const badges = { "/work-queue": myQueueCount, "/analysis": myNewLeadsCount, "/buybox": buyBoxNewCount, "/sell-land": sellLandNewCount };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-border bg-card transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}>
        <SidebarContent collapsed={collapsed} menus={menus} isAdmin={isAdmin} badges={badges} />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} menus={menus} isAdmin={isAdmin} badges={badges} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/95 backdrop-blur flex items-center gap-3 px-4 shrink-0">
          <button className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <button className="hidden md:inline-flex p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors" onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-toggle-btn">
            {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>

          <div className="flex-1" />

          <button
            onClick={() => setAngelaOpen(true)}
            data-testid="ask-angela-open"
            className="flex items-center gap-2 rounded-full border border-white/20 bg-transparent px-3.5 py-1.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Ask Angela</span>
          </button>

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors" data-testid="user-menu-btn">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                  {initials(user?.name || "U")}
                </div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-[11px] text-muted-foreground">{user?.role}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto slim-scroll">{children}</main>
      </div>
      <AskAngela open={angelaOpen} onClose={() => setAngelaOpen(false)} />
    </div>
  );
}
